import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  FAST_COMPLETED_EVENT,
  FastCompletedDetail,
  getCompletionSettings,
  shouldShowCompletionModal,
} from "@/lib/completion";
import { selectCompletionMessages, CompletionSelection } from "@/lib/completion/messages";

function fmtDuration(min: number) {
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return `${h}H ${String(m).padStart(2, "0")}M`;
}

interface ModalState {
  detail: FastCompletedDetail;
  selection: CompletionSelection;
}

export function CompletionRitualModal() {
  const [state, setState] = useState<ModalState | null>(null);

  useEffect(() => {
    const onCompleted = (e: Event) => {
      const detail = (e as CustomEvent<FastCompletedDetail>).detail;
      if (!detail) return;
      if (!shouldShowCompletionModal(detail)) return;
      const s = getCompletionSettings();
      const selection = selectCompletionMessages({
        durationMinutes: detail.durationMinutes,
        goalMinutes: detail.goalMinutes,
        goalMet: detail.goalMet,
        interrupted: detail.interrupted,
        seed: detail.endedAt,
        includeAtmosphere: s.completion_atmosphere_enabled,
        includeBenefit: s.completion_benefit_enabled,
      });
      setState({ detail, selection });
    };
    window.addEventListener(FAST_COMPLETED_EVENT, onCompleted as EventListener);
    return () => window.removeEventListener(FAST_COMPLETED_EVENT, onCompleted as EventListener);
  }, []);

  if (!state) return null;
  const { detail, selection } = state;
  const close = () => setState(null);

  const header = detail.interrupted
    ? "PROTOCOL INTERRUPTED"
    : detail.goalMet
    ? "TARGET REACHED"
    : "FAST COMPLETED";

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="completion-ritual-title"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-[1px]"
      onClick={close}
    >
      <div
        className="lab-panel w-full max-w-sm relative"
        onClick={(e) => e.stopPropagation()}
        style={{ boxShadow: "0 0 0 1px hsl(var(--panel-border)), 0 0 40px hsl(var(--terminal-green) / 0.08)" }}
      >
        <div className="lab-strip flex justify-between items-center px-3 py-1.5 text-[10px] tracking-[0.25em]">
          <span>{header}</span>
          <span className={detail.goalMet ? "text-[hsl(var(--terminal-green))]" : "text-[hsl(var(--terminal-amber))]"}>
            {detail.goalMet ? "● TARGET" : "○ LOGGED"}
          </span>
        </div>

        <div className="p-4 space-y-4">
          {/* Readout */}
          <div className="border border-[hsl(var(--panel-border))] bg-[hsl(var(--panel-inset))] p-3">
            <div className="lab-label text-[hsl(var(--muted-foreground))] mb-1">DURATION</div>
            <div className="text-2xl tracking-[0.15em] text-[hsl(var(--terminal-green))] font-mono">
              {fmtDuration(detail.durationMinutes)}
            </div>
            <div className="mt-2 flex justify-between text-[10px] tracking-[0.2em] text-[hsl(var(--terminal-dim))]">
              <span>GOAL: {fmtDuration(detail.goalMinutes)}</span>
              <span>{Math.min(999, Math.round((detail.durationMinutes / detail.goalMinutes) * 100))}%</span>
            </div>
          </div>

          {/* Messages */}
          <div className="space-y-2 text-[12px] leading-snug tracking-wide">
            {selection.atmosphere && (
              <p className="text-[hsl(var(--terminal-amber))] italic">
                &gt; {selection.atmosphere.text}
              </p>
            )}
            {selection.benefit && (
              <p className="text-[hsl(var(--foreground))]">
                <span className="lab-label text-[hsl(var(--terminal-dim))] mr-1">NOTE:</span>
                {selection.benefit.text}
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-1">
            <button
              onClick={close}
              className="lab-btn flex-1 !py-2 tracking-[0.2em] text-[hsl(var(--terminal-green))]"
            >
              CLOSE
            </button>
            <Link
              to="/log"
              onClick={close}
              className="lab-btn !py-2 tracking-[0.2em] text-center text-[hsl(var(--muted-foreground))]"
            >
              VIEW LOG
            </Link>
          </div>

          <div className="text-[9px] text-center text-[hsl(var(--terminal-dim))] tracking-[0.3em] pt-1">
            ENTRY LOGGED · {new Date(detail.endedAt).toLocaleString()}
          </div>
        </div>
      </div>
    </div>
  );
}
